import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Link
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTransactionLogs,
  fetchTransactionLogStations,
  fetchTransactionLogs
} from '../store/slices/transactionLogsSlice';

const columns = [
  { id: 'id', label: 'ID', sortable: true },
  { id: 'transaction_datetime', label: 'Transaction Datetime', sortable: true },
  { id: 'partner', label: 'Partner Name', sortable: false },
  { id: 'msisdn', label: 'MSISDN', sortable: false },
  { id: 'origin', label: 'Origin Station', sortable: false },
  { id: 'destination', label: 'Destination Station', sortable: false },
  { id: 'ticket_total', label: 'Total Tickets', sortable: true, align: 'right' },
  { id: 'trip', label: 'Trip', sortable: true },
  { id: 'quota_usage', label: 'Quota Usage', sortable: true, align: 'right' },
  { id: 'quota_type', label: 'Quota Type', sortable: false },
  { id: 'period', label: 'Period', sortable: false },
  { id: 'total_fare', label: 'Total Fare', sortable: true, align: 'right' },
  { id: 'status', label: 'Purchase Status', sortable: true },
  { id: 'transaction_status', label: 'Transaction Status', sortable: false }
];

const purchaseStatusOptions = [
  { value: 'ALL', label: 'All Purchase Status' },
  { value: '100', label: '100 - Default Error' },
  { value: '101', label: '101 - Missing JSON Header' },
  { value: '102', label: '102 - Invalid Input (schema validation)' },
  { value: '103', label: '103 - Invalid JSON Format' },
  { value: '104', label: '104 - Invalid origin or destination station code' },
  { value: '108', label: '108 - User not found' },
  { value: '109', label: '109 - User not eligible' },
  { value: '110', label: '110 - Database Error (transaction id allocation failure)' },
  { value: '111', label: '111 - Quota exceed' },
  { value: '112', label: '112 - Outside contract period' },
  { value: '113', label: '113 - Invalid secret key' },
  { value: '114', label: '114 - Invalid or expired token' },
  { value: '115', label: '115 - Transaction not found' },
  { value: '116', label: '116 - Data already deleted' },
  { value: '117', label: '117 - Duplicate partnerTransactionId' },
  { value: '118', label: '118 - Ticket Generation Error' },
  { value: '119', label: '119 - Ticket not yet available' }
];

const transactionStatusOptions = [
  { value: 'ALL', label: 'All Transaction Status' },
  { value: 'WAITING_TICKET', label: 'WAITING_TICKET' },
  { value: 'NOTIFYING_PARTNER', label: 'NOTIFYING_PARTNER' },
  { value: 'NOTIFYING_PARTNER_ERROR', label: 'NOTIFYING_PARTNER_ERROR' },
  { value: 'FINISH', label: 'FINISH' },
  { value: 'QR_SERVER_ERROR', label: 'QR_SERVER_ERROR' }
];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString('en-GB');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

const formatStation = (station) => {
  if (!station) return '-';
  return station.name || station.code || '-';
};

const formatPeriod = (period) => {
  if (!period) return '-';
  const { start, end } = period;
  if (!start && !end) return '-';
  return `${formatDate(start)} - ${formatDate(end)}`;
};

const TransactionLogPage = () => {
  const dispatch = useDispatch();
  const {
    items,
    meta,
    loading,
    error,
    query,
    exportStatus,
    exportError,
    stations,
    stationsLoading,
    stationsError
  } = useSelector((state) => state.transactionLogs);

  const [page, setPage] = useState(Math.max((query.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query.pageSize ?? 10);
  const [startDate, setStartDate] = useState(query.startDate);
  const [endDate, setEndDate] = useState(query.endDate);
  const [originStation, setOriginStation] = useState(query.originStation);
  const [destinationStation, setDestinationStation] = useState(query.destinationStation);
  const [purchaseStatus, setPurchaseStatus] = useState(query.purchaseStatus ?? 'ALL');
  const [transactionStatus, setTransactionStatus] = useState(query.transactionStatus ?? 'ALL');
  const [search, setSearch] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy);
  const [sortDirection, setSortDirection] = useState(query.sortDirection);
  const [dense, setDense] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const menuOpen = Boolean(menuAnchor);

  const stationOptions = useMemo(() => {
    const base = [{ value: 'ALL', label: 'All Stations' }];
    if (!Array.isArray(stations) || !stations.length) {
      return base;
    }

    const unique = new Map();
    stations.forEach((station) => {
      const code = station.code?.toUpperCase?.() || '';
      if (!code) return;
      if (!unique.has(code)) {
        unique.set(code, {
          value: code,
          label: station.name || code
        });
      }
    });

    return [base[0], ...unique.values()];
  }, [stations]);

  const isDateRangeInvalid =
    startDate && endDate && new Date(startDate) > new Date(endDate);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!stations || !stations.length) {
      dispatch(fetchTransactionLogStations());
    }
  }, [dispatch, stations]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      fetchTransactionLogs({
        page: page + 1,
        pageSize: rowsPerPage,
        startDate,
        endDate,
        originStation,
        destinationStation,
        purchaseStatus,
        transactionStatus,
        search: debouncedSearch.trim(),
        sortBy,
        sortDirection
      })
    );
  }, [
    dispatch,
    page,
    rowsPerPage,
    startDate,
    endDate,
    originStation,
    destinationStation,
    purchaseStatus,
    transactionStatus,
    debouncedSearch,
    sortBy,
    sortDirection,
    isDateRangeInvalid
  ]);

  useEffect(() => {
    if (typeof query.page === 'number') {
      const zeroBasedPage = Math.max(query.page - 1, 0);
      setPage((prev) => (prev === zeroBasedPage ? prev : zeroBasedPage));
    }
    if (typeof query.pageSize === 'number') {
      setRowsPerPage((prev) => (prev === query.pageSize ? prev : query.pageSize));
    }
  }, [query.page, query.pageSize]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleExport = () => {
    setMenuAnchor(null);
    if (isDateRangeInvalid) return;
    dispatch(
      exportTransactionLogs({
        startDate,
        endDate,
        originStation,
        destinationStation,
        purchaseStatus,
        transactionStatus,
        search: debouncedSearch.trim(),
        sortBy,
        sortDirection
      })
    );
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Transaction Log</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Transaction Log
          </Typography>
          <Typography color="text.secondary">
            Review individual ticketing transactions and quota usage.
          </Typography>
        </Box>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 6,
            sx: {
              boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background:
                'radial-gradient(circle at top right, rgba(59, 130, 246, 0.24), transparent 55%), #ffffff',
              borderRadius: 1,
              mt: 1
            }
          }}
        >
          <MenuItem onClick={handleExport} disabled={exportStatus === 'loading'}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
          </MenuItem>
        </Menu>
      </Stack>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ lg: 'center' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}
            >
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(event) => {
                  setPage(0);
                  setStartDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(event) => {
                  setPage(0);
                  setEndDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="origin-station-filter">Origin Station</InputLabel>
                <Select
                  labelId="origin-station-filter"
                  label="Origin Station"
                  value={originStation}
                  onChange={(event) => {
                    setOriginStation(event.target.value);
                    setPage(0);
                  }}
                  disabled={stationsLoading}
                >
                  {stationOptions.map((option) => (
                    <MenuItem key={option.value || 'ALL'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="destination-station-filter">Destination Station</InputLabel>
                <Select
                  labelId="destination-station-filter"
                  label="Destination Station"
                  value={destinationStation}
                  onChange={(event) => {
                    setDestinationStation(event.target.value);
                    setPage(0);
                  }}
                  disabled={stationsLoading}
                >
                  {stationOptions.map((option) => (
                    <MenuItem key={option.value || 'ALL'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ lg: 'center' }}
          >
            <TextField
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search by transaction ID or MSISDN"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: '100%', md: 500 } }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="purchase-status-filter">Purchase Status</InputLabel>
                <Select
                  labelId="purchase-status-filter"
                  label="Purchase Status"
                  value={purchaseStatus}
                  onChange={(event) => {
                    setPurchaseStatus(event.target.value);
                    setPage(0);
                  }}
                >
                  {purchaseStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="transaction-status-filter">Transaction Status</InputLabel>
                <Select
                  labelId="transaction-status-filter"
                  label="Transaction Status"
                  value={transactionStatus}
                  onChange={(event) => {
                    setTransactionStatus(event.target.value);
                    setPage(0);
                  }}
                >
                  {transactionStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {isDateRangeInvalid && (
            <Alert severity="error">End date must be on or after start date.</Alert>
          )}
          {error && !loading && <Alert severity="error">{error}</Alert>}
          {exportError && <Alert severity="error">{exportError}</Alert>}
          {stationsError && !stationsLoading && <Alert severity="warning">{stationsError}</Alert>}

          {(loading || stationsLoading) && <LinearProgress />}

          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <TableContainer>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sortDirection={sortBy === column.id ? sortDirection : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(15, 23, 42, 0.03)'
                        }
                      }}
                    >
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{formatDateTime(row.transaction_datetime)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{row.partner?.name || 'Unknown'}</Typography>
                      </TableCell>
                      <TableCell>{row.msisdn || '-'}</TableCell>
                      <TableCell>{formatStation(row.origin)}</TableCell>
                      <TableCell>{formatStation(row.destination)}</TableCell>
                      <TableCell align="right">{row.ticket_total.toLocaleString()}</TableCell>
                      <TableCell>{row.trip || '-'}</TableCell>
                      <TableCell align="right">{row.quota_usage.toLocaleString()}</TableCell>
                      <TableCell>{row.quota_type || '-'}</TableCell>
                      <TableCell>{formatPeriod(row.period)}</TableCell>
                      <TableCell align="right">{row.total_fare.toLocaleString()}</TableCell>
                      <TableCell>{row.status_desc || row.status || '-'}</TableCell>
                      <TableCell>{row.transaction_status || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {!items.length && !loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            No transaction logs found
                          </Typography>
                          <Typography color="text.secondary">
                            Adjust the filters or search criteria to view additional records.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <FormControlLabel
              control={<Switch checked={dense} onChange={(event) => setDense(event.target.checked)} />}
              label="Dense"
            />

            <TablePagination
              component="div"
              count={meta?.total_items ?? 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                const nextRows = parseInt(event.target.value, 10);
                setRowsPerPage(nextRows);
                setPage(0);
              }}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 0, sm: 2 }
                }
              }}
            />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default TransactionLogPage;
